import React, { useState } from "react";
import { BsTelephone } from "react-icons/bs";
import { DonutCard, formatNumber } from "./support_sales_widgets";
import { CHART_COLORS } from "./charts/palette";

const number = (value) => Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : 0;

function duration(value) {
  const seconds = Math.floor(number(value));
  return `${formatNumber(Math.floor(seconds / 3600))} ساعت و ${formatNumber(Math.floor(seconds / 60) % 60)} دقیقه و ${formatNumber(seconds % 60)} ثانیه`;
}

function CallPeriodCard({ title, stats }) {
  const answered = number(stats.answered);
  const measured = number(stats.talk_measured);
  return (
    <article aria-label={title} className="min-w-0 rounded-xl border border-app-border bg-app-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-app-border pb-3">
        <h4 className="text-sm font-semibold text-app-text">{title}</h4>
        <span className="rounded-lg bg-app-primary-soft px-3 py-1 text-lg font-bold tabular-nums text-app-primary">
          {formatNumber(number(stats.total))} <span className="text-xs font-normal">تماس</span>
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          ["پاسخ‌داده‌شده", answered, "text-teal-600"],
          ["بی‌پاسخ / ناموفق", number(stats.unanswered), "text-amber-600"],
          ["وضعیت نامشخص", number(stats.unknown), "text-app-text-muted"],
        ].map(([label, value, color]) => (
          <div key={label} className="rounded-lg bg-app-muted p-2">
            <dt className="text-[11px] leading-5 text-app-text-muted">{label}</dt>
            <dd className={`mt-1 text-xl font-semibold tabular-nums ${color}`}>{formatNumber(value)}</dd>
          </div>
        ))}
      </dl>
      <dl className="mt-5 border-t border-app-border pt-3">
        <dt className="text-xs text-app-text-muted">مجموع مکالمه ثبت‌شده</dt>
        <dd className="mt-2 text-sm font-semibold tabular-nums text-app-text">
          {measured > 0 ? duration(stats.talk_seconds) : "ثبت نشده"}
        </dd>
      </dl>
      <p className="mt-2 text-[11px] leading-6 text-app-text-subtle">
        مدت {formatNumber(measured)} از {formatNumber(answered)} تماس پاسخ‌داده‌شده موجود است.
      </p>
    </article>
  );
}

export default function SimotelCallStats({ calls, period }) {
  const [selectedSource, setSelectedSource] = useState(null);
  const source = selectedSource || (number(calls?.imported?.month?.total) > 0 ? "imported" : "live");
  const report = calls?.[source];
  const available = calls?.available === true && report?.today && report?.month;
  const metric = report?.[period] || {};

  return (
    <section aria-labelledby="simotel-call-stats-title" className="space-y-3 pt-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 id="simotel-call-stats-title" className="flex items-center gap-2 text-base font-semibold text-app-text">
            <BsTelephone className="text-teal-600" aria-hidden="true" /> آمار تماس‌های سیموتل
          </h3>
          <p className="mt-1 text-xs leading-6 text-app-text-muted">بر اساس زمان شروع تماس · امروز و ماه جاری شمسی</p>
        </div>
        {available && (
          <div role="group" aria-label="منبع آمار تماس‌ها" className="inline-flex rounded-lg border border-app-border bg-app-surface p-1">
            {[["imported", "گزارش واردشده"], ["live", "وب‌هوک زنده"]].map(([value, label]) => (
              <button key={value} type="button" aria-pressed={source === value} onClick={() => setSelectedSource(value)}
                className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${source === value ? "bg-app-primary text-app-on-primary" : "text-app-text-muted hover:bg-app-muted"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {!available ? (
        <p role="status" className="rounded-xl border border-app-border bg-app-surface p-5 text-sm leading-7 text-app-text-muted">
          آمار تماس‌های سیموتل در دسترس نیست. برای دریافت دوباره، داشبورد را به‌روزرسانی کنید.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
            <CallPeriodCard title="تماس‌های این ماه" stats={report.month} />
            <CallPeriodCard title="تماس‌های امروز" stats={report.today} />
            <DonutCard title="نتیجه تماس‌ها" periodLabel={period === "month" ? "این ماه" : "امروز"} measure="count" countUnit="تماس"
              emptyText="تماسی در این بازه ثبت نشده است" segments={[
                { label: "پاسخ‌داده‌شده", value: number(metric.answered), color: CHART_COLORS.teal },
                { label: "بی‌پاسخ / ناموفق", value: number(metric.unanswered), color: CHART_COLORS.pink },
                { label: "وضعیت نامشخص", value: number(metric.unknown), color: CHART_COLORS.purple },
              ]} />
          </div>
          <p className="text-xs leading-6 text-app-text-subtle">
            گزارش واردشده و وب‌هوک جدا نمایش داده می‌شوند تا تماس‌های مشترک دوباره جمع نشوند.
            هر رکورد پایان تماس شمرده می‌شود؛ بخش‌های یک تماس دوطرفه می‌توانند رکوردهای جداگانه داشته باشند.
          </p>
          {number(report.undated) > 0 && (
            <p className="text-xs leading-6 text-amber-600">
              {formatNumber(number(report.undated))} رکورد در این منبع، زمان شروع معتبر ندارد و در آمار زمانی محاسبه نشده است.
            </p>
          )}
        </>
      )}
    </section>
  );
}
